import { useGT } from 'gt-react';
import { IOType } from '@erp/shared-types/node-graph';
import { cn } from 'src/lib/utils';
import { IOSpan } from 'src/lib/node-graph/io-span';
import { sourceKey } from 'src/lib/workflows/expressionBuilder/builderScope';
import {
  useSocketExpressionBuilderActions,
  useSocketExpressionBuilderView,
} from './builderContext';

/**
 * The step node's socket labels, extended for socket expressions: an input
 * opens the builder (its expression is shown by the badge beside the socket,
 * not repeated in the row); an output becomes pickable while a builder slot
 * waits for a source. Outside a builder-enabled canvas they are the plain
 * labels.
 */

export function InputSocketLabel({
  nodeId,
  socketId,
  socketName,
  override,
  isFlowSocket,
}: {
  nodeId: string;
  socketId: string;
  socketName: string;
  /** The socket's manual override, when it has one. */
  override: string | undefined;
  isFlowSocket: boolean;
}) {
  const gt = useGT();
  const actions = useSocketExpressionBuilderActions();
  const view = useSocketExpressionBuilderView();
  const editing =
    view?.editing?.nodeId === nodeId && view.editing.socketId === socketId;
  // Admins build; everyone else may open an existing expression to read it.
  const canOpen =
    !!actions && !isFlowSocket && (actions.canEdit || override !== undefined);

  const label = canOpen ? (
    <button
      type="button"
      className={cn(
        'nodrag -mx-0.5 rounded px-0.5 text-left hover:bg-stone-100 dark:hover:bg-stone-800',
        'focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none',
        editing &&
          'bg-blue-50 font-semibold text-blue-700 dark:bg-blue-950/60 dark:text-blue-200',
      )}
      onClick={(event) => {
        event.stopPropagation();
        actions?.open(nodeId, socketId);
      }}
      aria-label={
        actions?.canEdit
          ? gt('Build the expression for {socket}', {
              context:
                'step node input socket: opens the socket expression builder',
              socket: socketName,
            })
          : gt('View the expression for {socket}', {
              context:
                'step node input socket: opens the socket expression builder read-only',
              socket: socketName,
            })
      }
    >
      <IOSpan text={socketName} ioType={IOType.INPUT} />
    </button>
  ) : (
    <IOSpan text={socketName} ioType={IOType.INPUT} />
  );

  return label;
}

export function OutputSocketLabel({
  nodeId,
  socketId,
  socketName,
}: {
  nodeId: string;
  socketId: string;
  socketName: string;
}) {
  const gt = useGT();
  const actions = useSocketExpressionBuilderActions();
  const view = useSocketExpressionBuilderView();
  const key = sourceKey(nodeId, socketId);
  const picking = view?.pickableSources ?? null;
  const used = view?.editing ? view.usedSources.has(key) : false;

  if (actions && picking?.has(key)) {
    return (
      <button
        type="button"
        className="nodrag rounded bg-blue-50 px-1 font-medium text-blue-700 ring-1 ring-blue-300 hover:bg-blue-100 dark:bg-blue-950/60 dark:text-blue-200 dark:ring-blue-800"
        onClick={(event) => {
          event.stopPropagation();
          actions.pickSource(nodeId, socketId);
        }}
        aria-label={gt('Use {socket} in the expression', {
          context:
            'step node output socket: pick it for the waiting expression slot',
          socket: socketName,
        })}
      >
        <IOSpan text={socketName} ioType={IOType.OUTPUT} />
      </button>
    );
  }
  return (
    <span
      className={cn(
        picking && 'opacity-40',
        used &&
          'rounded bg-blue-50 px-1 font-semibold text-blue-700 dark:bg-blue-950/60 dark:text-blue-200',
      )}
    >
      <IOSpan text={socketName} ioType={IOType.OUTPUT} />
    </span>
  );
}
